/**
 * Weekly Plan Service
 *
 * Pure business logic for weekly planning calculations and transformations.
 * No React or Firebase dependencies.
 */

import { WeeklyPlan, DayTheme } from '@/types';

// ============================================
// Constants
// ============================================

const DAYS_IN_WEEK = 7;

// ============================================
// Week ID Functions
// ============================================

/**
 * Get the ISO week ID for a given date in "YYYY-Www" format.
 * Uses ISO 8601 week numbering (weeks start on Monday).
 *
 * @param date - The date to get the week ID for
 * @returns Week ID in "YYYY-Www" format (e.g., "2024-W03")
 */
export function getWeekId(date: Date): string {
  // Create a copy to avoid mutating the original
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));

  // Set to nearest Thursday: current date + 4 - current day number
  // Make Sunday's day number 7
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);

  // Get first day of year
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));

  // Calculate full weeks to nearest Thursday
  const weekNo = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);

  // Return ISO week format
  return `${d.getUTCFullYear()}-W${String(weekNo).padStart(2, '0')}`;
}

/**
 * Get the start (Monday) and end (Sunday) dates for a given week ID.
 *
 * @param weekId - Week ID in "YYYY-Www" format
 * @returns Object with start and end dates in YYYY-MM-DD format
 * @throws Error if weekId format is invalid
 */
export function getWeekBounds(weekId: string): { start: string; end: string } {
  // Parse week ID
  const match = weekId.match(/^(\d{4})-W(\d{2})$/);
  if (!match) {
    throw new Error(`Invalid week ID format: ${weekId}. Expected YYYY-Www`);
  }

  const year = parseInt(match[1], 10);
  const week = parseInt(match[2], 10);

  // Find January 4th of the year (always in week 1 per ISO 8601)
  const jan4 = new Date(Date.UTC(year, 0, 4));

  // Find the Monday of week 1
  const dayOfWeek = jan4.getUTCDay() || 7; // Make Sunday = 7
  const week1Monday = new Date(jan4);
  week1Monday.setUTCDate(jan4.getUTCDate() - dayOfWeek + 1);

  // Calculate the Monday of the requested week
  const weekMonday = new Date(week1Monday);
  weekMonday.setUTCDate(week1Monday.getUTCDate() + (week - 1) * 7);

  // Calculate Sunday
  const weekSunday = new Date(weekMonday);
  weekSunday.setUTCDate(weekMonday.getUTCDate() + 6);

  // Format as YYYY-MM-DD
  const formatDate = (d: Date): string => {
    const yyyy = d.getUTCFullYear();
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  return {
    start: formatDate(weekMonday),
    end: formatDate(weekSunday),
  };
}

/**
 * Get all dates in a week as YYYY-MM-DD strings.
 *
 * @param weekId - Week ID in "YYYY-Www" format
 * @returns Array of 7 date strings (Monday through Sunday)
 */
export function getWeekDates(weekId: string): string[] {
  const { start } = getWeekBounds(weekId);
  const startDate = new Date(start + 'T00:00:00Z');
  const dates: string[] = [];

  for (let i = 0; i < DAYS_IN_WEEK; i++) {
    const date = new Date(startDate);
    date.setUTCDate(startDate.getUTCDate() + i);
    const yyyy = date.getUTCFullYear();
    const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(date.getUTCDate()).padStart(2, '0');
    dates.push(`${yyyy}-${mm}-${dd}`);
  }

  return dates;
}

// ============================================
// ID Generation
// ============================================

/**
 * Generate a unique goal ID.
 *
 * @returns Unique goal ID string
 */
export function generateGoalId(): string {
  return `goal_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

// ============================================
// Factory Functions
// ============================================

/**
 * Create a default weekly plan structure (without Firestore timestamps).
 * The caller is responsible for adding createdAt/updatedAt timestamps.
 *
 * @param userId - The user ID for the plan
 * @param weekId - The week ID in "YYYY-Www" format
 * @returns A partial WeeklyPlan object (missing id and timestamps)
 */
export function createDefaultWeeklyPlan(
  userId: string,
  weekId: string
): Omit<WeeklyPlan, 'id' | 'createdAt' | 'updatedAt'> {
  const { start, end } = getWeekBounds(weekId);
  const dates = getWeekDates(weekId);

  // Initialize dayThemes with null for each day
  const dayThemes: Record<string, DayTheme> = {};
  for (const date of dates) {
    dayThemes[date] = null;
  }

  return {
    userId,
    weekStart: start,
    weekEnd: end,
    goals: [],
    dayThemes,
    notes: '',
  };
}
